import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Search,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Filter,
  Download,
  Eye,
  Smartphone,
  Bell,
  AlertCircle,
  Loader2,
  RotateCcw,
  CalendarRange,
  List // Added for page size icon
} from 'lucide-react';
import { apiFetch } from '../api';
import { ensureSocketConnection } from '../websocket/socketUtils';
import { useWebSocket } from '../websocket/WebSocketContext';
import FCMDetails from './Fcmdetails';
import ExportBlockedModal from '../components/Exportblockedmodal';

export default function FCMHistory({ onBack, setError, loginId, deliveryMode }) {

  const { client } = useWebSocket();

  const [fcmData, setFcmData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  // Export-blocked modal
  const [showExportBlocked, setShowExportBlocked] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [referenceSearch, setReferenceSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [appNames, setAppNames] = useState([]);
  const [selectedApp, setSelectedApp] = useState('ALL');
  const [isAppNamesLoading, setIsAppNamesLoading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // Now mutable, default 10
  const [totalCount, setTotalCount] = useState(0); // Kept for export/info only

  const [selectedNotification, setSelectedNotification] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());

  const fetchAppNames = async () => {
    setIsAppNamesLoading(true);
    setError('');

    try {
      if (deliveryMode === 'socket') {
        // --- WEBSOCKET LOGIC ---
        try {
          await ensureSocketConnection(client);
        } catch (connError) {
          console.error(connError);
          setError('Socket connection failed.');
          setIsAppNamesLoading(false);
          return;
        }

        const topic = `/topic/getAppName/${loginId}`;
        const subscription = client.subscribe(topic, (msg) => {
          try {
            const payload = JSON.parse(msg.body);
            const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

            if (data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
              const names = ['ALL', ...data.Response.map(item => item.appName).filter(Boolean)];
              setAppNames(names);
              setSelectedApp(prev => prev || names[0]);
            } else {
              setError('Failed to load app names via socket.');
            }
          } catch (e) {
            console.error('Failed to parse app names socket', e);
          } finally {
            subscription.unsubscribe();
            setIsAppNamesLoading(false);
          }
        });

        // Publish request (Empty body is fine for get-all)
        client.publish({
          destination: '/app/getAppName',
          body: JSON.stringify({})
        });

      } else {
        // --- API LOGIC ---
        const response = await apiFetch(`/getAppName`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        if (data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
          const appNames = ['ALL', ...data.Response.map(item => item.appName).filter(Boolean)];
          setAppNames(appNames);
          setSelectedApp(prev => prev || appNames[0]);
        } else {
          setError('Failed to load app names. Invalid response format.');
        }
        setIsAppNamesLoading(false);
      }
    } catch (err) {
      console.error('Error fetching app names:', err);
      setError('Failed to load app names. Network error or API issue.');
      setIsAppNamesLoading(false);
    }
  };

  const dateToMs = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T00:00:00').getTime().toString();
  };

  const endDateToMs = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T23:59:59').getTime().toString();
  };

  const fetchFcmHistory = async (page = currentPage, size = pageSize) => {
    setIsLoading(true);

    // 1. Build Payload (Common for both API and Socket)
    const pageNo = page - 1; // Adjusting 1-based page to 0-based index if needed
    const filterPayload = {
      pageNo,
      pageSize: size
    };

    if (searchTerm.trim()) {
      filterPayload.searchText = searchTerm.trim();
    }
    if (referenceSearch.trim()) {
      filterPayload.referenceIdOrName = referenceSearch.trim();
    }
    if (selectedApp) {
      filterPayload.appName = selectedApp === 'ALL' ? '' : selectedApp;
    }
    if (statusFilter) {
      filterPayload.status = statusFilter;
    }
    if (startDate) {
      filterPayload.startTimeMs = dateToMs(startDate);
    }
    if (endDate) {
      filterPayload.endTimeMs = endDateToMs(endDate);
    }

    try {
      if (deliveryMode === 'socket') {
        // --- WEBSOCKET LOGIC ---
        try {
          await ensureSocketConnection(client);
        } catch (connError) {
          console.error(connError);
          setError('Socket connection failed.');
          setIsLoading(false);
          return;
        }

        const topic = `/topic/fcm/filter/${loginId}`;
        const subscription = client.subscribe(topic, (msg) => {
          try {
            const payload = JSON.parse(msg.body);
            const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

            if (data && typeof data === 'object') {
              if (data.Response && Array.isArray(data.Response)) {
                setFcmData(data.Response);
                setTotalCount(data.TOTAL_COUNT || 0);
              } else {
                setFcmData([]);
                setTotalCount(0);
              }
            } else {
              setFcmData([]);
              setTotalCount(0);
            }
          } catch (e) {
            console.error('Failed to parse FCM history socket', e);
            setError('Failed to parse history data.');
          } finally {
            subscription.unsubscribe();
            setIsLoading(false);
          }
        });

        // Publish to /app/fcm/filter
        client.publish({
          destination: '/app/fcm/filter',
          body: JSON.stringify(filterPayload)
        });

      } else {
        // --- API LOGIC ---
        const url = `/fcm/filter`;
        const res = await apiFetch(url, {
          method: 'POST',
          body: JSON.stringify(filterPayload)
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({ message: 'No error details available' }));
          throw new Error(`HTTP error! status: ${res.status}, message: ${errorData.message || res.statusText}`);
        }

        const data = await res.json();

        if (data && typeof data === 'object') {
          if (data.Response && Array.isArray(data.Response)) {
            setFcmData(data.Response);
            setTotalCount(data.TOTAL_COUNT || 0);
          } else {
            setFcmData([]);
            setTotalCount(0);
          }
        } else {
          setFcmData([]);
          setTotalCount(0);
        }
        setIsLoading(false);
      }

    } catch (error) {
      console.error('Error fetching FCM history:', error);
      setError('Failed to fetch FCM history: ' + error.message);
      setFcmData([]);
      setTotalCount(0);
      setIsLoading(false);
    }
  };

  const handleExport = async () => {

    // Block export in socket/stream mode — show modal instead
    if (deliveryMode === 'socket') {
      setShowExportBlocked(true);
      return;
    }

    if (!startDate || !endDate) {
      setError('Please select both start and end dates for export');
      return;
    }

    setIsExporting(true);
    try {
      const filterPayload = {
        pageNo: 0,
        pageSize: 1000 // Default high limit for export
      };

      if (searchTerm.trim()) {
        filterPayload.searchText = searchTerm.trim();
      }

      if (referenceSearch.trim()) {
        filterPayload.referenceIdOrName = referenceSearch.trim();
      }

      if (selectedApp) {
        filterPayload.appName = selectedApp === 'ALL' ? '' : selectedApp;
      }

      if (statusFilter) {
        filterPayload.status = statusFilter;
      }

      filterPayload.startTimeMs = dateToMs(startDate);
      filterPayload.endTimeMs = endDateToMs(endDate);

      const url = `/fcm/filter?export=true`;

      const res = await apiFetch(url, {
        method: 'POST',
        body: JSON.stringify(filterPayload)
      });

      if (!res.ok) {
        throw new Error(`Export failed: ${res.status} ${res.statusText}`);
      }

      const data = await res.json();

      if (data.STATUS === 'SUCCESS' && data.Response) {
        const byteCharacters = atob(data.Response);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `fcm-history-${startDate}-to-${endDate}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error('Export failed: Invalid response format');
      }

    } catch (error) {
      console.error('Error exporting data:', error);
      setError('Failed to export data: ' + error.message);
    } finally {
      setIsExporting(false);
    }
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setReferenceSearch('');
    setStartDate('');
    setEndDate('');
    setStatusFilter('');
    setSelectedApp('ALL');
    setSelectedItems(new Set());
    setCurrentPage(1);
    setPageSize(10); // Reset page size
  };

  useEffect(() => {
    // Debounce for text searches, immediate for others
    const delay = searchTerm || referenceSearch ? 500 : 0;

    const debounceTimer = setTimeout(() => {
      fetchFcmHistory(currentPage, pageSize);
    }, delay);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, referenceSearch, startDate, endDate, statusFilter, selectedApp, currentPage, pageSize]);

  useEffect(() => {
    fetchAppNames();
  }, []);

  const handleRefresh = () => {
    fetchFcmHistory(currentPage, pageSize);
  };

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setIsLoading(true); // <--- ADD THIS: Lock UI immediately
    setPageSize(newSize);
    // Usually good to reset to page 1 when changing size
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handlePreviousPage = () => {
    // Prevent action if already loading or at page 1
    if (currentPage > 1 && !isLoading) {
      setIsLoading(true); // <--- ADD THIS: Lock UI immediately
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    // Prevent action if already loading or no more data
    // We check !isLoading to prevent double clicks
    if (fcmData.length >= pageSize && !isLoading) {
      setIsLoading(true); // <--- ADD THIS: Lock UI immediately
      setCurrentPage(currentPage + 1);
    }
  };

  const getStatusColor = (response, status) => {
    if (status === 'SUCCESS' || response?.toLowerCase().includes('successfully') || response?.toLowerCase().includes('sent successfully')) {
      return 'text-green-400';
    }
    if (status === 'FAILED' || response?.toLowerCase().includes('error') || response?.toLowerCase().includes('invalid')) {
      return 'text-red-400';
    }
    return 'text-yellow-400';
  };

  const getStatusIcon = (response, status) => {
    if (status === 'SUCCESS' || response?.includes('succesfully')) {
      return CheckCircle;
    }
    return XCircle;
  };

  const truncateToken = (token) => {
    if (!token || typeof token !== 'string') return '';
    return `${token.substring(0, 15)}...${token.substring(token.length - 15)}`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleItemSelect = (id) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedItems.size === fcmData.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(fcmData.map(item => item._id)));
    }
  };

  const isExportEnabled = startDate && endDate && !isExporting;

  return (
    <div className="min-h-screen">

      {/* Export-blocked modal — shown when in socket mode */}
      <ExportBlockedModal
        isOpen={showExportBlocked}
        onClose={() => setShowExportBlocked(false)}
        feature="FCM Export"
      />

      {/* Header */}
      <header className="bg-white/10 backdrop-blur-md border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="flex items-center space-x-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-all duration-200"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Dashboard</span>
              </button>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                  <Bell className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">FCM History</h1>
                  <p className="text-sm text-white/60">Push Notification History</p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={handleExport}
                disabled={!isExportEnabled && deliveryMode !== 'socket'}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${deliveryMode === 'socket'
                  ? 'bg-orange-500/20 text-orange-200 hover:bg-orange-500/30 cursor-pointer'
                  : isExportEnabled
                    ? 'bg-green-500/20 text-green-200 hover:bg-green-500/30'
                    : 'bg-gray-500/20 text-gray-400 cursor-not-allowed'
                  }`}
              >
                {isExporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span>{isExporting ? 'Exporting...' : 'Export'}</span>
                {deliveryMode === 'socket' && (
                  <span className="ml-1 text-[10px] font-bold bg-orange-500/30 text-orange-300 px-1.5 py-0.5 rounded-full border border-orange-500/30">
                    API only
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Search by Title/Body */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">Search Title/Body</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search notifications..."
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Search by Reference ID or Name */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">Reference ID or Name</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50" />
                <input
                  type="text"
                  value={referenceSearch}
                  onChange={(e) => setReferenceSearch(e.target.value)}
                  placeholder="Search by reference..."
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </div>

            {/* App Name Filter */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">App Name</label>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50" />
                <select
                  value={selectedApp}
                  onChange={(e) => setSelectedApp(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
                >
                  {appNames.length === 0 ? (
                    <option className="bg-slate-800" disabled>Loading...</option>
                  ) : (
                    appNames.map((app, index) => (
                      <option key={index} value={app} className="bg-slate-800">
                        {app === 'ALL' ? 'All Apps' : app}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">Start Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50 pointer-events-none" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
                />
              </div>
            </div>

            {/* End Date */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">End Date</label>
              <div className="relative">
                <CalendarRange className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50 pointer-events-none" />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">Status</label>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
                >
                  <option value="" className="bg-slate-800">All Status</option>
                  <option value="SUCCESS" className="bg-slate-800">Success</option>
                  <option value="FAILED" className="bg-slate-800">Failed</option>
                  <option value="PENDING" className="bg-slate-800">Pending</option>
                </select>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="md:col-span-2 lg:col-span-3 flex items-end space-x-2">
              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-purple-500/20 text-purple-200 rounded-lg hover:bg-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
              <button
                onClick={handleResetFilters}
                className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-orange-500/20 text-orange-200 rounded-lg hover:bg-orange-500/30 transition-all duration-200"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Reset</span>
              </button>
            </div>
          </div>

          {/* Export Info */}
          {!isExportEnabled && (
            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                <span className="text-yellow-200 text-sm">
                  Please select both start and end dates to enable export functionality
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-white/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <h2 className="text-xl font-bold text-white">
                  FCM Notifications
                  <span className="ml-3 text-sm text-white/60">
                    Showing {fcmData.length} record{fcmData.length !== 1 ? 's' : ''}
                  </span>
                </h2>

                {/* Removed total count from header */}
                {fcmData.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedItems.size === fcmData.length && fcmData.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-white/20 bg-white/10 text-blue-500 focus:ring-blue-500"
                    />
                    <span className="text-white/60 text-sm">
                      {selectedItems.size > 0 ? `${selectedItems.size} selected` : 'Select all'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Loading State */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center space-x-3">
                <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
                <span className="text-white/80">Loading FCM history...</span>
              </div>
            </div>
          ) : fcmData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="w-12 h-12 text-white/40 mb-4" />
              <p className="text-white/60 text-lg">No notifications found</p>
              <p className="text-white/40 text-sm">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {fcmData.map((notification) => {
                const StatusIcon = getStatusIcon(notification.fcmResponse, notification.STATUS);
                return (
                  <div
                    key={notification._id}
                    className="px-6 py-4 hover:bg-white/5 transition-all duration-200"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 pt-1">
                        <input
                          type="checkbox"
                          checked={selectedItems.has(notification._id)}
                          onChange={() => handleItemSelect(notification._id)}
                          className="rounded border-white/20 bg-white/10 text-blue-500 focus:ring-blue-500"
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                              <Smartphone className="w-5 h-5 text-white" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-semibold truncate">
                              {notification.title}
                            </h3>
                            <p className="text-white/60 text-sm truncate">
                              {notification.body}
                            </p>
                          </div>
                          <div className="flex-shrink-0 flex items-center space-x-2">
                            <StatusIcon className={`w-5 h-5 ${getStatusColor(notification.fcmResponse, notification.STATUS)}`} />
                            <span className={`text-sm font-medium ${getStatusColor(notification.fcmResponse, notification.STATUS)}`}>
                              {notification.STATUS || (notification.fcmResponse?.includes('succesfully') ? 'Success' : 'Failed')}
                            </span>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                          <div>
                            <p className="text-white/40 text-xs uppercase tracking-wide">Device Token</p>
                            <p className="text-white/80 text-sm font-mono">
                              {truncateToken(notification.device_token)}
                            </p>
                          </div>
                          <div>
                            <p className="text-white/40 text-xs uppercase tracking-wide">App Name</p>
                            <p className="text-white/80 text-sm">{notification.appName}</p>
                          </div>
                          <div>
                            <p className="text-white/40 text-xs uppercase tracking-wide">Type</p>
                            <p className="text-white/80 text-sm">{notification.fcm_type}</p>
                          </div>
                        </div>

                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center space-x-4 text-white/60 text-sm">
                            <div className="flex items-center space-x-1">
                              <Clock className="w-4 h-4" />
                              <span>{formatDate(notification.creation_time_str)}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Calendar className="w-4 h-4" />
                              <span>{notification.date}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => setSelectedNotification(notification)}
                            className="flex items-center space-x-1 px-3 py-1 bg-purple-500/20 text-purple-200 rounded-lg hover:bg-purple-500/30 transition-all duration-200 text-sm"
                          >
                            <Eye className="w-4 h-4" />
                            <span>View Details</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {!isLoading && fcmData.length > 0 && (
            <div className="px-6 py-4 border-t border-white/20 bg-white/5">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">

                {/* Page Size Selector */}
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2 text-white/60 text-sm">
                    <List className="w-4 h-4" />
                    <span>Rows per page:</span>
                  </div>
                  <select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    className="bg-white/10 border border-white/20 text-white rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  >
                    <option value={10} className="bg-slate-800">10</option>
                    <option value={25} className="bg-slate-800">25</option>
                    <option value={50} className="bg-slate-800">50</option>
                    <option value={100} className="bg-slate-800">100</option>
                  </select>
                </div>

                {/* Buttons */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1 || isLoading}
                    className="px-3 py-1 bg-white/10 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    Previous
                  </button>
                  <span className="text-white/80 px-3 py-1 font-medium">
                    Page {currentPage}
                  </span>
                  <button
                    onClick={handleNextPage}
                    // Next is disabled if we got fewer items than the page size (meaning end of list)
                    disabled={fcmData.length < pageSize || isLoading}
                    className="px-3 py-1 bg-white/10 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedNotification && (
        <FCMDetails
          selectedNotification={selectedNotification}
          onClose={() => setSelectedNotification(null)}
        />
      )}

    </div>
  );
}